import React from "react";
import styled from "styled-components";

/**
 * オファー契約フォームの共通レイアウト部品。
 *
 * オファー作成ウィザードの詳細フォーム（`steps/DetailFormStep`）と、締結後の
 * 契約更新モーダル（`ContractAmendModal`）で **同じ見た目・同じ項目構成** に
 * するために共有する。片方だけスタイルが変わると「契約書を作るとき」と
 * 「契約書を更新するとき」で別の画面に見えてしまうため、ここを唯一の定義とする。
 */

export const FormRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
`;

export const FormSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 12px;
  background: #fff;
  border: 1px solid var(--soremo-border);
  border-radius: 8px;
`;

export const FormSectionTitle = styled.div.attrs({
  className: "heading-spacing-1824",
})`
  color: var(--soremo-text);
`;

export const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabelRow = styled.label.attrs({
  className: "bodytext-13 u-line-height-100 u-pl4",
})`
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  color: var(--soremo-text);
`;

const RequiredBadge = styled.span.attrs({
  className: "label8 u-pl8 u-text-blue",
})``;

const OptionalBadge = styled.span.attrs({
  className: "label8 u-pl8 u-text-light-gray",
})``;

export const FieldLabel: React.FC<{
  text: string;
  option?: "required" | "optional";
}> = ({ text, option }) => (
  <FieldLabelRow>
    {text}
    {option === "required" && <RequiredBadge>必須</RequiredBadge>}
    {option === "optional" && <OptionalBadge>任意</OptionalBadge>}
  </FieldLabelRow>
);

export const InlineRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  align-items: end;
`;

export const Accordion = styled.details`
  background: #fff;
  border: 1px solid var(--soremo-border);
  border-radius: 8px;
  padding: 24px 12px;

  &[open] summary .material-symbols-rounded {
    transform: rotate(180deg);
  }
`;

export const AccordionSummary = styled.summary.attrs({
  className: "heading-spacing-1824",
})`
  cursor: pointer;
  list-style: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  &::-webkit-details-marker {
    display: none;
  }

  .material-symbols-rounded {
    font-size: 24px;
    color: var(--soremo-light-gray);
    transition: transform 0.2s ease;
  }
`;

export const AccordionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 16px;
`;

// danger 表示はスタイルガイド A-2 に従い `u-text-green`（soremo の danger 色は緑）。
// 背景は白、枠線は中立色のみ。赤系の塗り・枠は使わない。
export const BalanceNoticeWarning = styled.div.attrs({
  className: "bodytext-13 u-text-green",
})`
  margin-top: 8px;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid var(--soremo-border);
  background: #fff;
`;
